/**
 * @fileoverview Windy Request Library
 * @version 1.1.7
 * @author windy
 * @license MIT
 * @lastModified 2025-03-16 12:33:26
 *
 * 更新日志：
 * 1.1.7 (2025-03-11)
 * - 使用 Map 替代 sessionStorage 存储防抖数据，提升性能
 * - 添加防抖数据自动清理机制，防止内存泄漏
 * - 使用 URLSearchParams API 替代手动拼接参数，提高安全性
 * - 优化参数处理逻辑，支持更好的嵌套对象处理
 *
 * 1.0.0 (2024-03-19)
 * - 初始版本
 * - 实现了基础功能
 *
 * 0.9.0 (2024-03-18)
 * - beta版本
 * - 完成主要功能开发
 */
export const REQUEST_VERSION = '1.1.7';

import axios from 'axios';
import CryptoJS from 'crypto-js';
import {nanoid} from 'nanoid';
import moment from 'moment';

// 常量
const REQUEST_CONSTANTS = {
    TOKEN_KEY: 'X-Windy-Token',// 根据业务需求修改
    ANTI_SHAKE_KEY: 'X-Anti-Shake',// 根据业务需求修改
    DEFAULT_TIMEOUT: 10000,
    SUCCESS_CODE: 200
};

// 创建 axios 实例
const axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_BASE_API,
    timeout: REQUEST_CONSTANTS.DEFAULT_TIMEOUT,
    headers: {
        'Accept': 'application/json, text/html;q=0.9',     // 可接受的响应类型
        'Content-Type': 'application/json; charset=utf-8', // 发送请求的内容类型
        'X-Environment': process.env.NODE_ENV, // 当前环境
    },
    responseType: 'json',// 约定响应数据格式
});

// 1. 首先在文件顶部添加防抖数据存储的 Map
const antiShakeMap = new Map();

// 2. 修改防抖检查函数
function antiShakeCheck(config, interval = 2000) {
    // 1.0 生成请求的唯一标识
    const {method, url, params, data} = config;
    const paramsString = params ? Object.keys(params).sort().map(key => `${key}:${params[key]}`).join('&') : '';
    const dataString = method === 'post' && data ? JSON.stringify(data) : '';
    const rawKey = `${method.toUpperCase()} ${url}?${paramsString}&${dataString}`;
    const requestKey = CryptoJS.SHA256(rawKey).toString();

    const now = moment();

    // 2.0 获取上次请求记录
    const lastCall = antiShakeMap.get(requestKey);

    // 3.0 如果没有记录,创建新记录并放行
    if (!lastCall) {
        const newRecord = {
            datetime: now.format('YYYY-MM-DD HH:mm:ss'),
            version: nanoid(10)
        };
        antiShakeMap.set(requestKey, newRecord);

        // 设置过期时间，自动清理
        setTimeout(() => {
            antiShakeMap.delete(requestKey);
        }, interval + 1000); // 比间隔多一秒，确保完全覆盖

        return true;
    }

    // 4.0 计算时间差
    const lastCallTime = moment(lastCall.datetime);
    const timeDiff = now.diff(lastCallTime);

    // 5.0 判断是否过期
    if (timeDiff > interval) {
        // 过期则更新记录并放行
        const newRecord = {
            datetime: now.format('YYYY-MM-DD HH:mm:ss'),
            version: nanoid(10)
        };
        antiShakeMap.set(requestKey, newRecord);

        // 设置过期时间，自动清理
        setTimeout(() => {
            antiShakeMap.delete(requestKey);
        }, interval + 1000);

        return true;
    }

    // 6.0 未过期则拦截
    return false;
}

// 3. 使用 URLSearchParams 优化参数处理函数
function tansParams(params) {
    if (!params) {
        return '';
    }

    const searchParams = new URLSearchParams();

    // 递归处理对象参数
    const appendParam = (key, value) => {
        if (value === null || value === '' || typeof value === 'undefined') {
            return;
        }

        if (typeof value === 'object') {
            Object.keys(value).forEach(subKey => {
                if (value[subKey] !== null && value[subKey] !== '' && typeof value[subKey] !== 'undefined') {
                    searchParams.append(`${key}[${subKey}]`, value[subKey]);
                }
            });
        } else {
            searchParams.append(key, value);
        }
    };

    // 处理所有参数
    Object.keys(params).forEach(key => {
        appendParam(key, params[key]);
    });

    return searchParams.toString();
}

// 如果是开发环境则执行日志打印
export function devLog(...data) {
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'development') {
        console.log(...data);
    }
}

// 从Axios的config中获取GET请求参数,并拼接到url后面
function handleGetParams(config) {
    if (config.method.toLowerCase() === 'get' && config.params) {
        let url = config.url + '?' + tansParams(config.params);
        config.params = {};
        config.url = url;
    }
    return config;
}

// 请求拦截器
axiosInstance.interceptors.request.use(
    // 往 requestConfig 挂载方法和属性,以便在响应拦截器中也能使用
    requestConfig => {
        // 设置默认的成功提示和错误提示,如果需要自定义,则在请求配置中传入,自行实现对应的antd或者element的提示组件
        if (!requestConfig.successNotificationFn) {
            requestConfig.successNotificationFn = (cfg) => devLog('Success:', cfg);
        }
        if (!requestConfig.errorNotificationFn) {
            requestConfig.errorNotificationFn = (cfg) => devLog('Error:', cfg);
        }

        // 从 localStorage 中获取 token 并添加到请求头中
        requestConfig.headers[REQUEST_CONSTANTS.TOKEN_KEY] = localStorage.getItem(REQUEST_CONSTANTS.TOKEN_KEY) || '';

        // 根据`是否处理防抖`触发防抖功能
        const antiShake = requestConfig.headers[REQUEST_CONSTANTS.ANTI_SHAKE_KEY];
        if (antiShake && !antiShakeCheck(requestConfig)) {
            let cfg = {
                "code": 429,
                "success": false,
                "data": null,
                "msg": '请求频繁',
            }
            requestConfig.errorNotificationFn(cfg);
            return Promise.reject({message: '请求频繁'});
        }

        // 如果是GET请求,且有参数,则将参数拼接到url后面
        requestConfig = handleGetParams(requestConfig);

        // 返回请求配置
        devLog('dev log request config:', requestConfig);
        return requestConfig;
    },
    error => {
        devLog('dev log request error:', error);
        return Promise.reject(error);
    }
);


// 响应拦截器:约定 response.data 格式: {"code": 200,"success": true,"data": null,"msg": "success"}
axiosInstance.interceptors.response.use(
    response => {
        //  Axios 会在响应对象中包含原始的请求配置信息
        const originalRequestConfig = response.config;

        const code = response?.data?.code || REQUEST_CONSTANTS.SUCCESS_CODE;
        // 如果不是200,认为是业务错误,提示错误信息
        if (code !== REQUEST_CONSTANTS.SUCCESS_CODE) {
            let cfg = {
                "code": code,
                "success": response?.data?.success || null,
                "data": response?.data?.data || null,
                "msg": response?.data?.msg || '未知错误',
            };
            // 提示错误信息
            originalRequestConfig.errorNotificationFn(cfg);
            return Promise.reject(response.data);
        } else {
            // 一些场景不需要频繁打印成功信息,通过 originalRequestConfig 中的 doNotSuccessNotification 控制
            if (!originalRequestConfig.doNotSuccessNotification) {
                let cfg = {
                    "code": code,
                    "success": response.data.success || null,
                    "data": response.data.data || null,
                    "msg": response.data.msg || 'done',
                };
                // 提示成功信息
                originalRequestConfig.successNotificationFn(cfg);
            }
            return response.data;
        }
    },
    error => {
        devLog('dev log response error:', error);
        let {message} = error;// 解构赋值: 从 error 中提取 message 属性,并赋值给 message 变量

        devLog('error:', error);
        devLog('error.response:', error.response);


        if (message === "Network Error") {
            message = "网络异常";
        } else if (message.toLowerCase().includes('timeout')) {
            message = "请求超时";
        } else if (error.response?.data?.msg) {
            message = error.response.data.msg;// 如果返回来业务错误信息,则显示业务错误信息
        } else if (message.toLowerCase().includes('request failed with status code')) {
            message = "HTTP " + message.substr(message.length - 3) + " 异常";
        } else if (!error.response) {
            message = "服务器无响应，请检查网络连接";
        }

        message = message || '未知错误';
        // 提示错误信息
        let cfg = {
            "code": error.response?.data?.code || 500,
            "success": error.response?.data?.success || false,
            "data": error.response?.data?.data || null,
            "msg": message,
        };
        error.config.errorNotificationFn(cfg);
        return Promise.reject(error);
    }
);

// 导出 常量
export {REQUEST_CONSTANTS};

export default axiosInstance;